import { supabase } from "@/integrations/supabase/client";
import type { OrderStatus } from "@/types/tms";

/**
 * Shared leg progression logic.
 *
 * Used by both the driver-side status updates (Ready to Depart / I've Arrived)
 * and the yard check-in flow. The pure helpers below are exported so they
 * can be exhaustively unit-tested without touching the Supabase client.
 *
 * Identity contract: callers MUST pass `{ parentOrderId, legId }` explicitly.
 * `legId` is null for true single-leg orders that pre-date the legs table or
 * for callers that don't carry a leg id. The orchestrator still resolves
 * sibling legs from the DB so the parent-promotion logic is identical for
 * single-leg and multi-leg orders.
 */

// Canonical rank table lives in `order-status.ts`; re-exported here so the
// many existing importers of `leg-progression`'s STATUS_RANK keep working
// without a second copy drifting out of sync.
export { STATUS_RANK } from "@/lib/order-status";
import { STATUS_RANK } from "@/lib/order-status";


export interface LegRow {
  id: string;
  leg_sequence: number;
  driver_status: OrderStatus | string | null;
  actual_departure_time?: string | null;
  actual_arrival_time?: string | null;
}

export interface ParentDecisionInput {
  siblingLegs: LegRow[];
  legId: string | null;
  newLegStatus: OrderStatus;
  currentParentStatus: OrderStatus | string | null | undefined;
}

export interface ParentDecision {
  isMultiLeg: boolean;
  /** The projected status of every sibling leg after this update. */
  projectedStatuses: (OrderStatus | string | null)[];
  /** Computed next parent status, or null if the parent should not change. */
  nextParentStatus: OrderStatus | null;
  /** Whether the parent update should actually be written. */
  shouldPromoteParent: boolean;
}

/**
 * Pure function — given the current state of sibling legs and the new leg
 * status, decide what the parent order's next status should be and whether
 * we should write that promotion.
 *
 * Rules:
 *  - Single-leg (0 or 1 sibling): the parent mirrors the action.
 *  - Multi-leg: parent → delivered iff every projected leg is delivered;
 *    otherwise parent → in_transit if any projected leg has started
 *    (in_transit OR delivered) while others are still open. A delivered
 *    leg alongside an unstarted sibling used to leave the parent stuck at
 *    `confirmed` after a yard check-in.
 *  - Never regress: skip the write if the new status ranks ≤ current.
 *  - Terminal states (delivered, canceled) are never overwritten.
 */
export function computeParentDecision(input: ParentDecisionInput): ParentDecision {
  const { siblingLegs, legId, newLegStatus, currentParentStatus } = input;
  const isMultiLeg = siblingLegs.length > 1;

  const projectedStatuses = siblingLegs.map((l) =>
    l.id === legId ? newLegStatus : (l.driver_status as OrderStatus | string | null),
  );

  let nextParentStatus: OrderStatus | null = null;
  if (!isMultiLeg) {
    nextParentStatus = newLegStatus;
  } else if (
    projectedStatuses.length > 0 &&
    projectedStatuses.every((s) => s === "delivered" || s === "canceled") &&
    projectedStatuses.some((s) => s === "delivered")
  ) {
    nextParentStatus = "delivered";
  } else if (projectedStatuses.some((s) => s === "in_transit" || s === "delivered")) {
    nextParentStatus = "in_transit";
  }

  const cur = currentParentStatus ?? "";
  const shouldPromoteParent =
    !!nextParentStatus &&
    cur !== "delivered" &&
    cur !== "canceled" &&
    (STATUS_RANK[nextParentStatus] ?? -1) > (STATUS_RANK[cur] ?? -1);

  return { isMultiLeg, projectedStatuses, nextParentStatus, shouldPromoteParent };
}

/**
 * Pure function — figure out whether THIS leg is the boundary leg that
 * should fire a trailer event. Boundary rules:
 *   - in_transit  → only on the FIRST leg
 *   - delivered   → only on the LAST leg
 * Single-leg orders are both first and last, so they always fire.
 */
export function isBoundaryLeg(
  siblingLegs: LegRow[],
  legId: string | null,
  newLegStatus: OrderStatus,
): boolean {
  if (siblingLegs.length <= 1) return true;
  const thisLeg = legId ? siblingLegs.find((l) => l.id === legId) : null;
  if (!thisLeg) return false;
  const first = siblingLegs[0];
  const last = siblingLegs[siblingLegs.length - 1];
  if (newLegStatus === "in_transit") return thisLeg.leg_sequence === first.leg_sequence;
  if (newLegStatus === "delivered") return thisLeg.leg_sequence === last.leg_sequence;
  return false;
}

export interface ProgressLegInput {
  parentOrderId: string;
  legId: string | null;
  newStatus: OrderStatus;
  actorLabel: string;
  tenantId: string | null | undefined;
  /**
   * When true, reject `delivered` on a leg whose prior `driver_status` is
   * neither `in_transit` nor `delivered`. Driver-initiated flows enable
   * this so a stale UI overlay can never skip in_transit and silently
   * collapse departure==arrival (see AOP-DETM-01494 / 01496). Yard /
   * terminal flows that legitimately stamp delivery without a prior
   * in_transit transition leave it `false`.
   */
  requirePriorInTransit?: boolean;
  /**
   * Yard flows: route the leg + parent writes through the
   * `yard_progress_leg_delivered` SECURITY DEFINER RPC instead of direct
   * table updates. `terminal_staff` has no UPDATE grant on `legs` / `orders`,
   * so a direct update silently matched zero rows and the check-in appeared
   * to succeed while nothing changed (see TA232191 / AOP-SCNN-01000).
   */
  viaYardRpc?: boolean;
}

export interface ProgressLegResult {
  isMultiLeg: boolean;
  legUpdated: boolean;
  parentPromoted: boolean;
  nextParentStatus: OrderStatus | null;
  oldParentStatus: OrderStatus | null;
  thisLeg: LegRow | null;
  isBoundary: boolean;
  /** Total legs on the parent order (yard RPC path only). */
  legCount?: number;
  /**
   * Sibling legs still open (not delivered / canceled) after this update.
   * A leg with a null driver_status counts as open.
   */
  openLegCount?: number;
  /** Sequence number of the leg this call completed, when known. */
  legSequence?: number | null;
}

/**
 * Pure: build the user-facing message for a yard check-in so terminal staff
 * can tell whether the whole order finished or only this leg did.
 */
export function describeYardCheckInResult(
  trailerId: string,
  result: Pick<ProgressLegResult, "isMultiLeg" | "legCount" | "openLegCount" | "legSequence">,
): string {
  const base = `Trailer ${trailerId} checked in`;
  if (!result.isMultiLeg) return base;
  const open = result.openLegCount ?? 0;
  const legLabel =
    result.legSequence != null && result.legCount
      ? `Leg ${result.legSequence} of ${result.legCount} delivered`
      : "Leg delivered";
  if (open > 0) {
    return `${base} — ${legLabel}; ${open} leg${open === 1 ? "" : "s"} still open`;
  }
  return `${base} — ${legLabel}; order complete`;
}

/**
 * Resolve `{ parentOrderId, legId }` from an `OrderWithRelations`-shaped row
 * coming from the `orders_with_legs` read view. The view's mapper aliases
 * `id` to the leg id and stores the actual parent order id under
 * `parent_order_id`. For legacy rows that came directly from `orders`
 * (`parent_order_id` is null/undefined), `id` IS the parent order id and
 * there is no leg id to address.
 *
 * Callers should invoke this once at the top of the action and pass the
 * result to `progressLeg` so we never re-derive identity inside the
 * orchestrator (which made the original code convoluted).
 */
export function resolveLegIdentity(row: {
  id?: string | null;
  parent_order_id?: string | null;
}): { parentOrderId: string; legId: string | null } {
  const parentOrderId = (row.parent_order_id ?? row.id) as string;
  const legId = row.parent_order_id ? (row.id as string) : null;
  return { parentOrderId, legId };
}

/**
 * Orchestrator that performs the IO. Both the driver and yard flows call
 * this so leg promotion semantics + audit log shape stay in sync.
 *
 * Always:
 *   - Writes the leg's `driver_status` (when `legId` is provided), even
 *     for single-leg orders, so the legs table is the source of truth.
 *   - Writes a `leg_<n>_driver_status` entry to `order_change_log` for the
 *     leg transition (single OR multi).
 *   - Promotes the parent order according to `computeParentDecision`, and
 *     writes a `status` entry to the change log for the parent transition.
 *
 * Trailer events are NOT touched here — callers (driver hook, yard check-in)
 * own that side-effect because they have different boundary semantics
 * (driver fires on the leg boundary, yard fires per yard event).
 */
export async function progressLeg(input: ProgressLegInput): Promise<ProgressLegResult> {
  const { parentOrderId, legId, newStatus, actorLabel, tenantId, requirePriorInTransit, viaYardRpc } = input;

  if (viaYardRpc && newStatus === "delivered") {
    const { data, error } = await (supabase as any).rpc("yard_progress_leg_delivered", {
      p_order_id: parentOrderId,
      p_leg_id: legId,
      p_actor: actorLabel,
    });
    if (error) throw error;
    const res = (data ?? {}) as any;
    return {
      isMultiLeg: !!res.isMultiLeg,
      legUpdated: !!res.legUpdated,
      parentPromoted: !!res.parentPromoted,
      nextParentStatus: (res.nextParentStatus ?? null) as OrderStatus | null,
      oldParentStatus: (res.oldParentStatus ?? null) as OrderStatus | null,
      thisLeg: null,
      isBoundary: true,
      legCount: typeof res.legCount === "number" ? res.legCount : undefined,
      openLegCount: typeof res.openLegCount === "number" ? res.openLegCount : undefined,
      legSequence: res.legSequence ?? null,
    };
  }

  const nowIso = new Date().toISOString();

  const { data: siblingsRaw, error: siblingsErr } = await supabase
    .from("legs")
    .select("id, leg_sequence, driver_status, actual_departure_time, actual_arrival_time")
    .eq("order_id", parentOrderId)
    .order("leg_sequence", { ascending: true });
  if (siblingsErr) throw siblingsErr;
  const siblingLegs = (siblingsRaw ?? []) as LegRow[];

  const thisLeg = legId ? siblingLegs.find((l) => l.id === legId) ?? null : null;

  // Defense in depth (driver flows only): refuse to land "delivered" on a
  // leg that has not been marked in_transit first. The driver UI is
  // supposed to gate this via the per-leg `driver_status` overlay, but a
  // stale overlay (e.g. inheriting the parent's `in_transit` from a sibling
  // leg) used to surface the wrong action button and silently skip
  // in_transit, collapsing departure and arrival to the same timestamp.
  // See `useDriverLegStatusUpdate` (opt-in) vs `useYardAction` (opt-out).
  if (
    requirePriorInTransit &&
    legId &&
    thisLeg &&
    newStatus === "delivered" &&
    thisLeg.driver_status !== "in_transit" &&
    thisLeg.driver_status !== "delivered"
  ) {
    throw new Error(
      `Cannot mark leg ${thisLeg.leg_sequence} delivered: leg has not been marked in_transit (current driver_status=${thisLeg.driver_status ?? "null"})`,
    );
  }

  // 1) Persist the leg's driver_status. Always do this when we have a legId,
  //    regardless of multi-leg vs single-leg, so the legs table is canonical.
  //    Also stamp write-once actual_departure_time / actual_arrival_time
  //    mirroring poll-eapi-trip-status, so manual driver/yard progressions
  //    leave the same trail as EAPI-driven ones.
  let legUpdated = false;
  const legActualStamp: Record<string, string> = {};
  if (legId) {
    if (newStatus === "in_transit" && !thisLeg?.actual_departure_time) {
      legActualStamp.actual_departure_time = nowIso;
    }
    if (newStatus === "delivered") {
      if (!thisLeg?.actual_arrival_time) {
        legActualStamp.actual_arrival_time = nowIso;
      }
      // Backfill departure if we somehow skipped in_transit on this leg.
      if (
        thisLeg?.driver_status !== "in_transit" &&
        !thisLeg?.actual_departure_time
      ) {
        legActualStamp.actual_departure_time = nowIso;
      }
    }

    const { data: legRows, error: legErr } = await supabase
      .from("legs")
      .update({ driver_status: newStatus, ...legActualStamp })
      .eq("id", legId)
      .select("id");
    if (legErr) throw legErr;
    // RLS denials on UPDATE surface as zero affected rows, NOT an error.
    // Treat that as a hard failure so the caller shows an error instead of
    // a success toast for a write that never happened.
    if (!legRows || legRows.length === 0) {
      throw new Error("Leg status was not updated (no matching row — likely insufficient permissions)");
    }
    legUpdated = true;
  }

  // 2) Read parent order status, then decide.
  const { data: parent } = await supabase
    .from("orders")
    .select("status, actual_departure_time, actual_arrival_time")
    .eq("id", parentOrderId)
    .maybeSingle();
  const oldParentStatus = (parent?.status ?? null) as OrderStatus | null;
  const parentActualDeparture = (parent as any)?.actual_departure_time ?? null;
  const parentActualArrival = (parent as any)?.actual_arrival_time ?? null;

  const decision = computeParentDecision({
    siblingLegs,
    legId,
    newLegStatus: newStatus,
    currentParentStatus: oldParentStatus,
  });

  // 3) Promote the parent order if eligible.
  const parentActualStamp: Record<string, string> = {};
  if (decision.shouldPromoteParent && decision.nextParentStatus) {
    if (decision.nextParentStatus === "in_transit" && !parentActualDeparture) {
      parentActualStamp.actual_departure_time = nowIso;
    }
    if (decision.nextParentStatus === "delivered") {
      if (!parentActualArrival) {
        parentActualStamp.actual_arrival_time = nowIso;
      }
      // Backfill departure on direct confirmed → delivered transitions.
      if (oldParentStatus !== "in_transit" && !parentActualDeparture) {
        parentActualStamp.actual_departure_time = nowIso;
      }
    }

    const { data: orderRows, error: updErr } = await supabase
      .from("orders")
      .update({
        status: decision.nextParentStatus,
        ...(decision.nextParentStatus === "delivered" ? { eapi_eta: null } : {}),
        ...parentActualStamp,
      })
      .eq("id", parentOrderId)
      .select("id");
    if (updErr) throw updErr;
    if (!orderRows || orderRows.length === 0) {
      throw new Error("Order status was not updated (no matching row — likely insufficient permissions)");
    }
  }

  // 4) Audit log — write a leg-level row (single OR multi) and, if the
  //    parent moved, a status row. tenantId is required by RLS.
  if (legId && thisLeg && tenantId) {
    await supabase.from("order_change_log").insert({
      order_id: parentOrderId,
      field_name: `leg_${thisLeg.leg_sequence}_driver_status`,
      old_value: (thisLeg.driver_status as string | null) ?? null,
      new_value: newStatus,
      changed_by: actorLabel,
      tenant_id: tenantId,
    } as any);

    // Audit any leg-level actual timestamps we just stamped.
    for (const field of Object.keys(legActualStamp)) {
      await supabase.from("order_change_log").insert({
        order_id: parentOrderId,
        field_name: field,
        old_value: null,
        new_value: legActualStamp[field],
        changed_by: actorLabel,
        tenant_id: tenantId,
      } as any);
    }
  }
  if (decision.shouldPromoteParent && decision.nextParentStatus && tenantId) {
    await supabase.from("order_change_log").insert({
      order_id: parentOrderId,
      field_name: "status",
      old_value: oldParentStatus,
      new_value: decision.nextParentStatus,
      changed_by: actorLabel,
      tenant_id: tenantId,
    } as any);

    // Audit any parent-level actual timestamps we just stamped.
    for (const field of Object.keys(parentActualStamp)) {
      await supabase.from("order_change_log").insert({
        order_id: parentOrderId,
        field_name: field,
        old_value: null,
        new_value: parentActualStamp[field],
        changed_by: actorLabel,
        tenant_id: tenantId,
      } as any);
    }
  }

  return {
    isMultiLeg: decision.isMultiLeg,
    legUpdated,
    parentPromoted: decision.shouldPromoteParent,
    nextParentStatus: decision.nextParentStatus,
    oldParentStatus,
    thisLeg,
    isBoundary: isBoundaryLeg(siblingLegs, legId, newStatus),
  };
}